import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MotionConfig } from 'motion/react'
import { ErrorBoundary } from './components/ErrorBoundary'
import { consumeTokenFromUrl } from './api/auth'
import { dropLegacyWebToken } from './api/signIn'
import { shouldRetryQuery } from './api/busyRetry'
import { startAnalytics } from './analytics'
import { router } from './router'
import './styles/index.css'

// Capture a Sign in with Apple token delivered via URL fragment BEFORE the
// router mounts and the first queries fire.
consumeTokenFromUrl()
// A token a real sign-in left in localStorage before the web moved to cookies is dropped (#360).
dropLegacyWebToken()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,          // never refetch automatically — data changes only via sync or manual actions
      gcTime: 24 * 60 * 60 * 1000, // 24h — keep cache for the full session
      // One more try, as before, except for a request that already waited through
      // apiFetch's busy retries (#392): left to `retry: 1` the two layers multiply.
      retry: shouldRetryQuery,
      refetchOnWindowFocus: false,
    },
  },
})

// Analytics (#708), after the two calls above, so a Developer Mode fragment is gone from the address before anything
// reads it, and before the first render, so the first page view is this page's. Dark without a container id.
startAnalytics({ router, queryClient })

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {/* Reduce Motion, app-wide. motion's own default is "never", so until this every
            motion/react animation moved for a person who asked it not to, except in the few
            components that read useReducedMotion() themselves. "user" drops transform and layout
            animation and keeps opacity: DESIGN.md's "entrance animations drop to opacity-only". */}
        <MotionConfig reducedMotion="user">
          <RouterProvider router={router} />
        </MotionConfig>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
)
